/**
 * Offline capture queue (CLAUDE.md rule 3: capture never blocks on the
 * network).
 *
 * `enqueue` only writes to the local store (SQLite on native, IndexedDB on
 * the web) and returns. `sync` replays queued captures to the API in the
 * order they were made. A capture leaves the queue only after the server
 * confirms it; because the API is idempotent on `client_id`, replaying one
 * that was received but whose response was lost cannot create a duplicate.
 *
 * A capture can carry one photo. It is uploaded after the capture exists,
 * using an `upload_id` chosen when the capture was saved: the API maps it to
 * the same object key every time and records photos idempotently by key, so
 * an upload interrupted at any point can be retried without a duplicate.
 *
 * A capture can also carry one voice note, handled the same way: uploaded
 * under a fixed `upload_id` and attached idempotently. It goes before the
 * photo, because a voice-only note has no text without it.
 */
import type { CreateCaptureBody } from '@klunkers/shared'
import type { LocalStore } from '@/native/storage'

export interface QueuedPhoto {
  upload_id: string
  content_type: string
  /** The image itself, as a data URL (works in IndexedDB and SQLite alike). */
  data_url: string
}

export interface QueuedAudio {
  upload_id: string
  content_type: string
  duration_s: number
  data_url: string
}

export interface QueuedCapture {
  client_id: string
  /** Owner, so one rider's queued notes are never sent under another's account. */
  user_id: string
  body: CreateCaptureBody & { client_id: string; captured_at: string }
  queued_at: string
  attempts: number
  /** Set when the server rejected it as invalid. Kept, never dropped silently. */
  rejected?: string
  /** Server id once the capture itself has synced (the photo may still be pending). */
  capture_id?: string
  photo?: QueuedPhoto
  audio?: QueuedAudio
  /** Set once the voice note is attached, so a retry only resends the photo. */
  audio_sent?: boolean
}

export type SendResult = { ok: true } | { ok: false; retry: boolean; message: string }
export type CaptureSendResult =
  { ok: true; captureId: string } | { ok: false; retry: boolean; message: string }

export interface QueueDeps {
  store: LocalStore
  send: (body: QueuedCapture['body']) => Promise<CaptureSendResult>
  /** Upload a queued photo and attach it to the synced capture. */
  sendPhoto?: (photo: QueuedPhoto, captureId: string) => Promise<SendResult>
  /** Upload a queued voice note and attach it to the synced capture. */
  sendAudio?: (audio: QueuedAudio, captureId: string) => Promise<SendResult>
}

const PREFIX = 'queue:capture:'
const keyFor = (c: Pick<QueuedCapture, 'client_id' | 'body'>) =>
  `${PREFIX}${c.body.captured_at}:${c.client_id}`

export async function enqueue(
  deps: Pick<QueueDeps, 'store'>,
  userId: string,
  body: QueuedCapture['body'],
  photo?: QueuedPhoto,
  audio?: QueuedAudio,
): Promise<QueuedCapture> {
  const item: QueuedCapture = {
    client_id: body.client_id,
    user_id: userId,
    body,
    queued_at: new Date().toISOString(),
    attempts: 0,
    ...(photo ? { photo } : {}),
    ...(audio ? { audio } : {}),
  }
  await deps.store.set(keyFor(item), item)
  return item
}

export async function list(
  deps: Pick<QueueDeps, 'store'>,
  userId?: string,
): Promise<QueuedCapture[]> {
  const items: QueuedCapture[] = []
  for (const key of await deps.store.keys(PREFIX)) {
    const item = await deps.store.get<QueuedCapture>(key)
    if (item && (!userId || item.user_id === userId)) items.push(item)
  }
  return items
}

export interface SyncOutcome {
  sent: number
  remaining: number
  /** True if we stopped early because the network or server was unavailable. */
  interrupted: boolean
}

/** Send every queued capture (and its photo) for this user, oldest first. */
export async function sync(deps: QueueDeps, userId: string): Promise<SyncOutcome> {
  let sent = 0
  let interrupted = false
  const items = await list(deps, userId)
  for (let item of items) {
    if (item.rejected) continue
    // Step 1: the capture itself.
    if (!item.capture_id) {
      const result = await deps.send(item.body)
      if (!result.ok) {
        if (result.retry) {
          await deps.store.set(keyFor(item), { ...item, attempts: item.attempts + 1 })
          interrupted = true
          break
        }
        await deps.store.set(keyFor(item), {
          ...item,
          attempts: item.attempts + 1,
          rejected: result.message,
        })
        continue
      }
      item = { ...item, capture_id: result.captureId }
      if (item.photo || item.audio) await deps.store.set(keyFor(item), item)
    }
    // Step 2: its voice note, if any.
    if (item.audio && !item.audio_sent && deps.sendAudio) {
      const result = await deps.sendAudio(item.audio, item.capture_id!)
      if (!result.ok) {
        if (result.retry) {
          await deps.store.set(keyFor(item), { ...item, attempts: item.attempts + 1 })
          interrupted = true
          break
        }
        await deps.store.set(keyFor(item), {
          ...item,
          attempts: item.attempts + 1,
          rejected: result.message,
        })
        continue
      }
      item = { ...item, audio_sent: true }
      if (item.photo) await deps.store.set(keyFor(item), item)
    }
    // Step 3: its photo, if any.
    if (item.photo && deps.sendPhoto) {
      const result = await deps.sendPhoto(item.photo, item.capture_id!)
      if (!result.ok) {
        if (result.retry) {
          await deps.store.set(keyFor(item), { ...item, attempts: item.attempts + 1 })
          interrupted = true
          break
        }
        // The note is safe on the server; keep the photo and say why it failed.
        await deps.store.set(keyFor(item), {
          ...item,
          attempts: item.attempts + 1,
          rejected: result.message,
        })
        continue
      }
    }
    await deps.store.delete(keyFor(item))
    sent++
  }
  const remaining = (await list(deps, userId)).length
  return { sent, remaining, interrupted }
}

export async function discard(deps: Pick<QueueDeps, 'store'>, item: QueuedCapture): Promise<void> {
  await deps.store.delete(keyFor(item))
}
